import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import ModalMUI from '@material-ui/core/Modal'
import styled from 'styled-components'
import { useFieldArray, useWatch } from 'react-hook-form'
import round from '../../../../domain/utils/round'
import Button from '@material-ui/core/IconButton'
import Title from '../../atomics/Typography/Title'
import download from '../../../assets/icon/download.svg'
import trash from '../../../assets/icon/trash.svg'
import close from '../../../assets/icon/x.svg'
import Tooltip from '../../atomics/Tooltip/Tooltip'
import ReactImageMagnify from 'react-image-magnify'

const Center = styled('div')`
  display: flex;
`

const Modal = styled(ModalMUI)`
  z-index: 1500 !important;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
  overflow: none;

  @media (max-width: 700px) {
    margin: 1%;
    height: 100%;
    width: 100%;
  }
`
const Label = styled('label')`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.palette.text.primary};
`

const ModalTitle = styled(Title)`
  font-style: normal;
  font-weight: 400;
  font-size: 26px;
  line-height: 32px;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.palette.text.primary};
`

const Container = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const ImageInfoContainer = styled('div')`
  display: flex;
  flex-direction: column;
`

const TooltipFront = styled(Tooltip)`
  z-index: 9999 !important;
`
const ModalContainer = styled('div')`
  background-color: ${({ theme }) => theme.palette.background.default};
  box-shadow: 0px 0px 3.12996px 1.04332px rgba(0, 0, 0, 0.25);
  border-radius: 8.26805px;
  padding-left: 4%;
  padding-right: 4%;
  padding-bottom: 2%;
  padding-top: 2%;
  position: relative;
  margin-top: 1%;
  height: 90%;
  width: 50%;
  margin-bottom: 3rem;
  @media (max-width: 1300) {
    width: 60%;
  }
  @media (max-width: 1100) {
    width: 80%;
  }
  @media (max-width: 700px) {
    margin: 3%;
    width: 100%;
  }
`

const Image = styled(ReactImageMagnify)`
  margin-top: 2rem;
  margin-bottom: 2rem;
  object-fit: contain;
  height: 100%;
  width: 100%;
  @media (max-width: 700px) {
    margin: 1%;
  }
`
const DownloadImageBtn = styled(Button)`
  background-color: ${({ theme }) => theme?.palette?.background?.default};
`

const DeleteImageBtn = styled(Button)`
  background-color: ${({ theme }) => theme?.palette?.background?.default};
  margin-right: 3%;
  @media (max-width: 1100px) {
    margin-right: 40px;
  }
`

const CloseBtn = styled(Button)`
  position: absolute;
  top: 2%;
  right: 10px;
`

const ButtonContainer = styled('div')`
  display: flex;
`

const ModalImage = ({
  open,
  onClose,
  title,
  control,
  src,
  index,
  setMediaList,
  appendToDelete,
  media
}) => {
  const [size, setSize] = useState(0)

  const getSize = useCallback(async () => {
    const blob = await fetch(src).then(r => r.blob())
    return setSize(round(blob.size.valueOf() / 1024))
  }, [src, setSize])

  useEffect(getSize, [getSize])

  const productName = useWatch({ control, name: 'productName' })

  const downloadImage = () => {
    fetch(src, {
      method: 'GET',
      headers: {}
    })
      .then(response => {
        response.arrayBuffer().then(function (buffer) {
          const url = window.URL.createObjectURL(new Blob([buffer]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${title}.png`) //or any other extension
          document.body.appendChild(link)
          link.click()
        })
      })
      .catch(err => {
        throw new Error(err)
      })
  }

  const { remove } = useFieldArray({
    control,
    name: 'media'
  })

  const handleDeleteImage = () => {
    remove([index])
    appendToDelete(media)
    setMediaList(prev =>
      [...prev].filter((val, idx) => val && idx !== index)
    )
    onClose()
  }
  const widthPreview = media?.preview?.image?.width || '1000px'
  const heightPreview = media?.preview?.image?.height || '1000px'

  useEffect(() => {
    // Función que maneja el evento de la tecla "Esc"
    const handleEsc = event => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    // Agregar el event listener solo si el modal está abierto
    if (open) {
      window.addEventListener('keydown', handleEsc)
    }

    // Limpiar el event listener cuando se cierre el modal
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [open, onClose])

  return (
    <Center>
      <Modal
        disableAutoFocus
        disableEnforceFocus
        open={open}
        onClose={onClose}
        aria-labelledby={title}
      >
        <ModalContainer>
          <CloseBtn onClick={onClose}>
            <img src={close} alt="close" height={20} />
          </CloseBtn>
          <Container>
            <ImageInfoContainer>
              <ModalTitle>{`${productName} ${title}`}</ModalTitle>
              <Label>{`${widthPreview} px x ${heightPreview} px`}</Label>
              <Label>{`${size || media?.file?.size} kb`}</Label>
            </ImageInfoContainer>
            <ButtonContainer>
              <TooltipFront title="Descargar" placement="bottom">
                <DownloadImageBtn onClick={downloadImage}>
                  <img src={download} alt="download" height={25} />
                </DownloadImageBtn>
              </TooltipFront>
              <TooltipFront title="Eliminar" placement="bottom">
                <DeleteImageBtn onClick={handleDeleteImage}>
                  <img src={trash} alt="delete" height={25} />
                </DeleteImageBtn>
              </TooltipFront>
            </ButtonContainer>
          </Container>
          <Image
            src={src}
            {...{
              enlargedImagePosition: 'over',
              smallImage: {
                alt: 'Zoomable',
                isFluidWidth: true,
                src,
                width: widthPreview,
                height: heightPreview
              },
              largeImage: {
                src,
                width: widthPreview,
                height: heightPreview
              }
            }}
          />
        </ModalContainer>
      </Modal>
    </Center>
  )
}
ModalImage.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  title: PropTypes.string,
  control: PropTypes.any,
  index: PropTypes.number,
  media: PropTypes.any,
  errors: PropTypes.any,
  src: PropTypes.any,
  setMediaList: PropTypes.func,
  appendToDelete: PropTypes.any
}
ModalImage.defaultProps = {
  open: false,
  media: [],
  setMediaList: data => data
}
export default ModalImage
